import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import {
  getViolatedMandatoryRequirementsValidator,
  makeRequiredArray,
} from 'KnmErknmPackage/MainModule/common/violatedMandatoryRequirementsValidator';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';

import { IDecisionResultDecisionForm } from '../interfaces/IDecisionResultDecisionForm';
import { IDecisionFormReason } from '../interfaces/IDecisionFormReason';
import { makeRequiredQuillEditorDelta } from '../../../CorePackage/MainModule/validators/isRequiredQuillEditorDelta';
import { getDecisionOrganizationValidate } from './getDecisionOrganizationValidate';
import { IDecisionForm } from '../interfaces/IDecisionForm';
import { getSignersValidator } from '../../../KnmErknmPackage/MainModule/common/getSignersValidator';
import { getFamiliarizationNotificationsValidate } from '../../../KnmErknmPackage/MainModule/common/getFamiliarizationNotificationsValidate';

interface IDecisionResultDecisionValidatorOptions {
  values: IDecisionForm;
  isSupervisor?: boolean;
  isWatchOnly?: boolean;
  isNewDecision?: boolean;
  isLater4111ForDecision?: boolean;
  isDecisionStatusFillInProcess?: boolean;
  isDecisionStatusReadyForApprove?: boolean;
  settings: IAdminSetting[] | undefined;
  state: IRootState;
}

export const getDecisionResultDecisionValidator = (
  resultDecisions: IDecisionResultDecisionForm[] | undefined,
  basisDocumentDateTime: string | undefined,
  reasons: IDecisionFormReason[] | undefined,
  isUse5234153: boolean,
  isUseSigners: boolean,
  options?: IDecisionResultDecisionValidatorOptions,
) => {
  const {
    settings,
    state,
    values,
    isDecisionStatusFillInProcess,
    isDecisionStatusReadyForApprove,
    isNewDecision,
    isSupervisor,
    isWatchOnly,
    isLater4111ForDecision,
  } = options || {};

  return resultDecisions?.map(decisionItem => ({
    kind: makeRequired(REQUIRED_ERROR)(decisionItem?.kind),
    document: decisionItem.paperless ? undefined : makeRequired(REQUIRED_ERROR)(decisionItem?.document?.attachments),
    date: decisionItem.paperless
      ? undefined
      : composeValidators(
          makeRequired(REQUIRED_ERROR),
          reasons && reasons.length > 0
            ? composeValidators(
                reasons[0].prescriptionDate
                  ? makeNoSoonerThenValidator(
                      'Дата решения не может быть меньше даты документа, на основании которого проводится мероприятие',
                      () => reasons[0].prescriptionDate,
                    )
                  : () => undefined,
                !reasons[0].order
                  ? makeNoSoonerThenValidator(
                      'Дата решения не может быть меньше даты начала действия основания',
                      () => reasons[0].dateBegin,
                      undefined,
                      true,
                      DATE_FORMAT_LONG_FNS,
                    )
                  : () => undefined,
                !reasons[0].order
                  ? makeNoLaterThenValidator(
                      'Дата решения не может быть больше даты окончания действия основания',
                      () => reasons[0].dateEnd,
                    )
                  : () => undefined,
                reasons[0].order
                  ? composeValidators(
                      makeNoSoonerThenValidator(
                        'Дата решения не может быть меньше даты начала действия поручения',
                        () => reasons[0].order?.checkBeginDate || undefined,
                        undefined,
                        true,
                        DATE_FORMAT_LONG_FNS,
                      ),
                      makeNoLaterThenValidator(
                        'Дата решения не может быть больше даты окончания действия поручения',
                        () => reasons[0].order?.checkEndDate || undefined,
                      ),
                    )
                  : () => undefined,
              )
            : () => undefined,
          makeNoLaterThenValidator(
            'Дата должна быть меньше или равна дате вынесения решения',
            () => decisionItem?.injunction?.dateResolved,
          ),
          isUse5234153
            ? makeNoSoonerThenValidator(
                'Дата решения не может быть меньше даты документа, на основании которого проводится мероприятие',
                () => basisDocumentDateTime?.split(' ')[0],
              )
            : () => undefined,
        )(decisionItem?.date),
    notes:
      decisionItem?.kind?.code !== KindResultDecisionCodesEnum.D_7 &&
      decisionItem?.kind?.code !== KindResultDecisionCodesEnum.D_1 &&
      !decisionItem?.notes?.length
        ? REQUIRED_ERROR
        : undefined,
    number: makeRequired(REQUIRED_ERROR)(decisionItem?.number),
    fioSigner:
      (isUseSigners &&
        (decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_7 ||
          decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_1)) ||
      decisionItem.systemSignatureUsed
        ? undefined
        : makeRequired(REQUIRED_ERROR)(decisionItem?.fioSigner),
    titleSigner:
      (isUseSigners &&
        (decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_7 ||
          decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_1)) ||
      decisionItem.systemSignatureUsed
        ? undefined
        : makeRequired(REQUIRED_ERROR)(decisionItem?.titleSigner),
    signers:
      isUseSigners &&
      (decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_7 ||
        decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_1)
        ? getSignersValidator(decisionItem.signers)
        : undefined,
    eliminatedMandatoryRequirements:
      decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_7 ||
      decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_1
        ? composeValidators(
            makeRequiredArray('Поле не может быть пустым'),
            getViolatedMandatoryRequirementsValidator({
              isSigned: decisionItem?.paperless ? decisionItem.signed : undefined,
              isValidateRequiredViolatedMandatory: true,
              isDecisionMode: true,
              isProdReady: true,
              isNeedViolationPLanFixValidate: true,
              minFixPlanDate: decisionItem?.date,
              isPaperless: decisionItem?.paperless,
              isDecision: true,
              isActMode: false,
              isViolationEventsRequired: false,
            }),
          )(decisionItem?.eliminatedMandatoryRequirements)
        : undefined,

    injunction:
      decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_7 ||
      decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_1
        ? {
            // note: !decisionItem?.eliminatedMandatoryRequirements?.length
            //   ? makeRequired('Поле не может быть пустым')(decisionItem?.injunction?.note)
            //   : undefined,
            noteReactQuillEditor: !decisionItem?.eliminatedMandatoryRequirements?.length
              ? makeRequiredQuillEditorDelta('Поле не может быть пустым')(
                  decisionItem?.injunction?.noteReactQuillEditor,
                )
              : undefined,
            dateResolved: decisionItem.paperless
              ? undefined
              : makeRequired('Поле не может быть пустым')(decisionItem?.injunction?.dateResolved),
          }
        : undefined,
    familiarizationNotifications: getFamiliarizationNotificationsValidate({
      familiarizationNotifications: decisionItem?.familiarizationNotifications,
      signers: decisionItem?.signers,
      firstExtractDateTime: decisionItem?.firstExtractDateTime,
      isUseSigners:
        isUseSigners &&
        (decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_7 ||
          decisionItem?.kind?.code === KindResultDecisionCodesEnum.D_1),
    }),
    founder:
      isLater4111ForDecision &&
      decisionItem.founder &&
      state &&
      values &&
      decisionItem.subjectType?.id === KnmResultDecisionSubjectTypesEnum.FOUNDER
        ? getDecisionOrganizationValidate({
            item: decisionItem.founder,
            settings,
            state,
            values,
            isDecisionStatusFillInProcess,
            isDecisionStatusReadyForApprove,
            isNewDecision,
            isSupervisor,
            isWatchOnly,
            isFounderValidate: true,
          })
        : undefined,
  }));
};
